import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { BudgetAlert } from '@/hooks/useBudgetAlerts';
import { formatCurrency } from '@/lib/foco-calculations';

interface BudgetProgressCardProps {
  alert: BudgetAlert;
  currency?: string;
}

export function BudgetProgressCard({ alert, currency = 'CRC' }: BudgetProgressCardProps) {
  const getStatusColor = () => {
    switch (alert.status) {
      case 'exceeded':
        return 'text-red-500';
      case 'warning':
        return 'text-yellow-500';
      default:
        return 'text-green-500';
    }
  };

  const getProgressColor = () => {
    switch (alert.status) {
      case 'exceeded':
        return 'bg-red-500';
      case 'warning':
        return 'bg-yellow-500';
      default:
        return 'bg-green-500';
    }
  };

  const getIcon = () => {
    switch (alert.status) {
      case 'exceeded':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default:
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    }
  };

  return (
    <Card className="p-3 sm:p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {getIcon()}
          <span className="font-medium text-sm">{alert.categoryLabel}</span>
        </div>
        <span className={`text-sm font-semibold ${getStatusColor()}`}>
          {Math.round(alert.percentage)}%
        </span>
      </div>
      
      <div className="space-y-2">
        <Progress 
          value={Math.min(alert.percentage, 100)} 
          className="h-2"
          indicatorClassName={getProgressColor()}
        />
        
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Gastado: {formatCurrency(alert.spent, currency)}</span>
          <span>Límite: {formatCurrency(alert.limit, currency)}</span>
        </div>
        
        {alert.status === 'exceeded' && (
          <p className="text-xs text-red-500 font-medium">
            ¡Excedido por {formatCurrency(Math.abs(alert.remaining), currency)}!
          </p>
        )}
        {alert.status === 'warning' && (
          <p className="text-xs text-yellow-600">
            Te quedan {formatCurrency(alert.remaining, currency)}
          </p>
        )}
      </div>
    </Card>
  );
}
